// The save store's provider interface (decision 0033: "the save store is a provider interface with a local and a
// Supabase backend, so another backend can be added later"). A backend keeps records: opaque bytes under a key, with
// the write time, a revision (a hash of the bytes) and the document's metadata, so a slot list or a sync can compare
// records without decoding them. Keys starting with "__" are the store's own bookkeeping and stay out of `list()`.
import { hashState } from "@rpg/kernel";

/** One stored record. */
export interface SaveRecord {
  key: string;
  bytes: Uint8Array;
  /** Wall-clock ms when the document was written (not when it was copied by a sync). */
  savedAt: number;
  /** A hash of `bytes`: equal revisions mean equal records. */
  rev: string;
  meta: Record<string, string | number | boolean | null>;
}

/** A record without its bytes, for lists. */
export type RecordInfo = Omit<SaveRecord, "bytes"> & { size: number };

/** A storage backend: IndexedDB, a remote, memory. */
export interface SaveBackend {
  /** A name for reports ("indexeddb:green-valley", "stub-remote"). */
  readonly name: string;
  get(key: string): Promise<SaveRecord | null>;
  put(record: SaveRecord): Promise<void>;
  delete(key: string): Promise<void>;
  /** Every record but the store's own ("__" keys), without bytes. */
  list(): Promise<RecordInfo[]>;
}

/** The revision of some bytes. */
export const revOf = (bytes: Uint8Array): string => hashState(bytes);

/** A record's info. */
export const infoOf = (r: SaveRecord): RecordInfo => ({ key: r.key, savedAt: r.savedAt, rev: r.rev, meta: r.meta, size: r.bytes.byteLength });

/** Is this one of the store's own keys? */
export const isInternalKey = (key: string): boolean => key.startsWith("__");

/** Copy a record so a caller can't change what a backend holds. */
const copy = (r: SaveRecord): SaveRecord => ({ ...r, bytes: r.bytes.slice(), meta: { ...r.meta } });

/** A backend in memory (tests, and the default remote stub's storage). */
export function createMemoryBackend(name = "memory"): SaveBackend & { readonly records: Map<string, SaveRecord> } {
  const records = new Map<string, SaveRecord>();
  return {
    name,
    records,
    get: async (key) => {
      const r = records.get(key);
      return r ? copy(r) : null;
    },
    put: async (r) => void records.set(r.key, copy(r)),
    delete: async (key) => void records.delete(key),
    list: async () => [...records.values()].filter((r) => !isInternalKey(r.key)).map(infoOf),
  };
}

/** Wrap a promise-free IDBRequest. */
const done = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });

/**
 * The local backend on IndexedDB: one database, one object store of records keyed by `key`. Records are stored as
 * structured clones (bytes stay a Uint8Array). `factory` defaults to the page's `indexedDB`.
 */
export function createIndexedDbBackend(o: { db: string; store?: string; factory?: IDBFactory }): SaveBackend & { close(): void } {
  const storeName = o.store ?? "records";
  let opening: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> =>
    (opening ??= new Promise((resolve, reject) => {
      const factory = o.factory ?? (globalThis as { indexedDB?: IDBFactory }).indexedDB;
      if (!factory) return reject(new Error("IndexedDB is not available"));
      const req = factory.open(o.db, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(storeName)) req.result.createObjectStore(storeName, { keyPath: "key" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error(`IndexedDB: could not open ${o.db}`));
      req.onblocked = () => reject(new Error(`IndexedDB: opening ${o.db} is blocked by another tab`));
    }));
  const tx = async <T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    const t = db.transaction(storeName, mode);
    const result = await done(run(t.objectStore(storeName)));
    if (mode === "readwrite")
      await new Promise<void>((resolve, reject) => {
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error ?? new Error("IndexedDB transaction failed"));
        t.onabort = () => reject(t.error ?? new Error("IndexedDB transaction aborted"));
      });
    return result;
  };
  return {
    name: `indexeddb:${o.db}`,
    get: async (key) => ((await tx("readonly", (s) => s.get(key))) as SaveRecord | undefined) ?? null,
    put: async (r) => void (await tx("readwrite", (s) => s.put({ ...r, bytes: r.bytes.slice() }))),
    delete: async (key) => void (await tx("readwrite", (s) => s.delete(key))),
    list: async () => ((await tx("readonly", (s) => s.getAll())) as SaveRecord[]).filter((r) => !isInternalKey(r.key)).map(infoOf),
    close: () => {
      void opening?.then((db) => db.close());
      opening = null;
    },
  };
}

/** Switches a stub remote obeys, so a test can take it offline or slow it down. */
export interface StubRemoteControls {
  /** While true every call rejects, as a remote with no network would. */
  offline: boolean;
  /** Each call waits this long first, ms. */
  latencyMs: number;
  /** Calls made (reads and writes), for tests. */
  calls: number;
}

/**
 * A stand-in for a remote save service (decision 0033's Supabase backend, a live stop point until the keys and the
 * migration exist) over any backend: the same interface, plus offline and latency switches. Tests give it memory;
 * a page can give it a second IndexedDB database, so "the cloud" survives a reload.
 */
export function createStubRemote(storage: SaveBackend, name = "stub-remote"): SaveBackend & { controls: StubRemoteControls } {
  const controls: StubRemoteControls = { offline: false, latencyMs: 0, calls: 0 };
  const call = async <T>(f: () => Promise<T>): Promise<T> => {
    controls.calls++;
    if (controls.latencyMs > 0) await new Promise((r) => setTimeout(r, controls.latencyMs));
    if (controls.offline) throw new Error(`${name}: offline`);
    return f();
  };
  return {
    name,
    controls,
    get: (key) => call(() => storage.get(key)),
    put: (r) => call(() => storage.put(r)),
    delete: (key) => call(() => storage.delete(key)),
    list: () => call(() => storage.list()),
  };
}
